<script setup>
import { ref, computed, watch } from 'vue'
import { Head, router, useForm } from '@inertiajs/vue3'
import Layout   from '@pages/Shared/layouts/AdminLayout.vue'
import Table    from '@pages/Shared/ui/Table.vue'
import Modal    from '@pages/Shared/Modal.vue'
import Select   from '@pages/Shared/form/Select.vue'
import Input    from '@pages/Shared/form/Input.vue'
import Textarea from '@pages/Shared/form/Textarea.vue'
import Spinner  from '@pages/Shared/ui/Spinner.vue'
import { flashToast } from '@/toast.js'

const props = defineProps({
    applications:  Object,
    sessions:      Array,
    sections:      Array,
    classRooms:    Array,
    activeSession: Object,
    counts:        Object,
    statuses:      Array,
    filters:       Object,
})

// ── Filters ────────────────────────────────────────────────────────────────
const filters = ref({
    search: null, session_id: null, section_id: null, status: null,
    ...props.filters,
})

watch(() => props.filters, v => { filters.value = { ...v } }, { deep: true })

const applyFilters = () => {
    const cleaned = Object.fromEntries(
        Object.entries(filters.value).filter(([_, v]) => v !== null && v !== '')
    )
    router.get('/admin/applications', cleaned, {
        preserveState: true, preserveScroll: true, replace: true,
    })
}

const handleSearch = v => { filters.value.search = v; applyFilters() }
const handleFilter = f => { filters.value = { ...filters.value, ...f }; applyFilters() }

// ── Selected application ───────────────────────────────────────────────────
const selectedApp = ref(null)

// ── Schedule Interview Modal ───────────────────────────────────────────────
// Applications page handles scheduling — it is the first action after submission
const scheduleModal = ref(false)
const scheduleForm  = useForm({ scheduled_at: '', venue: '', invitation_note: '' })

const openSchedule = app => {
    selectedApp.value = app
    scheduleForm.reset()
    scheduleModal.value = true
}
const submitSchedule = () => {
    scheduleForm.post(`/admin/applications/${selectedApp.value.id}/interview/schedule`, {
        onSuccess: page => { scheduleModal.value = false; scheduleForm.reset(); flashToast(page.props.flash) },
    })
}

// ── Admit Modal ────────────────────────────────────────────────────────────
// Only available after interview is completed (status = interviewed)
const admitModal      = ref(false)
const selectedSection = ref(null)
const admitForm       = useForm({ class_room_id: null, notes: '' })

const openAdmit = app => {
    selectedApp.value = app
    selectedSection.value = null
    admitForm.reset()
    admitModal.value = true
}
const submitAdmit = () => {
    admitForm.post(`/admin/applications/${selectedApp.value.id}/admit`, {
        onSuccess: page => { admitModal.value = false; admitForm.reset(); flashToast(page.props.flash) },
    })
}

// ── Reject Modal ───────────────────────────────────────────────────────────
// Available at any active stage
const rejectModal = ref(false)
const rejectForm  = useForm({ rejection_reason: '' })

const openReject = app => {
    selectedApp.value = app
    rejectForm.reset()
    rejectModal.value = true
}
const submitReject = () => {
    rejectForm.post(`/admin/applications/${selectedApp.value.id}/reject`, {
        onSuccess: page => { rejectModal.value = false; rejectForm.reset(); flashToast(page.props.flash) },
    })
}

// ── Options ────────────────────────────────────────────────────────────────
const sectionOptions = computed(() =>
    (props.sections ?? []).map(s => ({ label: s.name, value: s.id }))
)
const classOptions = computed(() => {
    if (!selectedSection.value) return []
    const section = props.sections?.find(s => s.id === selectedSection.value)
    return (section?.classes ?? []).map(c => ({ label: c.name, value: c.id }))
})

const statusColor = status => ({
    draft:       'bg-gray-100 text-gray-600',
    submitted:   'bg-primary-50 text-primary-700',
    invited:     'bg-indigo-50 text-indigo-700',
    interviewed: 'bg-purple-50 text-purple-700',
    admitted:    'bg-green-50 text-green-700',
    rejected:    'bg-red-50 text-red-600',
    enrolled:    'bg-emerald-50 text-emerald-700',
}[status] ?? 'bg-gray-100 text-gray-600')

const columns = [
    { key: 'index',     label: '#',         type: 'index' },
    { key: 'applicant', label: 'Applicant', type: 'slot'  },
    { key: 'section',   label: 'Section',   type: 'slot'  },
    { key: 'session',   label: 'Session',   type: 'slot'  },
    { key: 'interview', label: 'Interview', type: 'slot'  },
    { key: 'status',    label: 'Status',    type: 'slot'  },
    { key: 'submitted', label: 'Submitted', type: 'slot'  },
    { key: 'actions',   label: '',          type: 'slot'  },
]
</script>

<template>
    <Layout>
        <Head title="Applications" />

        <!-- ── Schedule Interview Modal ─────────────────────────────────── -->
        <Modal v-model="scheduleModal" title="Schedule Interview" @close="scheduleModal = false">
            <form class="p-4 md:p-5" @submit.prevent="submitSchedule">
                <p class="text-sm text-gray-500 mb-4">
                    Scheduling interview for
                    <strong>{{ selectedApp?.applicant?.first_name }} {{ selectedApp?.applicant?.last_name }}</strong>
                </p>
                <Input label="Date & Time" v-model="scheduleForm.scheduled_at" type="datetime-local"
                    :error="scheduleForm.errors.scheduled_at" class="mb-4" />
                <Input label="Venue (optional)" v-model="scheduleForm.venue"
                    placeholder="e.g. Admin Office, Room 1" class="mb-4" />
                <Textarea label="Invitation Note (optional)" v-model="scheduleForm.invitation_note"
                    :rows="3" class="mb-4" />
                <button type="submit" :disabled="scheduleForm.processing"
                    class="w-full py-3 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white
                           text-sm font-semibold flex justify-center items-center gap-2 transition">
                    <Spinner v-if="scheduleForm.processing" :size="14" />
                    Schedule Interview
                </button>
            </form>
        </Modal>

        <!-- ── Admit Modal ───────────────────────────────────────────────── -->
        <Modal v-model="admitModal" title="Grant Admission" @close="admitModal = false">
            <form class="p-4 md:p-5" @submit.prevent="submitAdmit">
                <p class="text-sm text-gray-500 mb-4">
                    Admitting
                    <strong>{{ selectedApp?.applicant?.first_name }} {{ selectedApp?.applicant?.last_name }}</strong>
                    — applied for section <strong>{{ selectedApp?.section?.name }}</strong>
                </p>
                <Select label="Select Section" v-model="selectedSection"
                    :options="sectionOptions" placeholder="Select section" class="mb-3" />
                <Select label="Select Class" v-model="admitForm.class_room_id"
                    :options="classOptions" placeholder="Select class"
                    :disabled="!selectedSection" :error="admitForm.errors.class_room_id" class="mb-4" />
                <Textarea label="Notes (optional)" v-model="admitForm.notes"
                    :rows="3" placeholder="Any notes for this admission..." class="mb-4" />
                <button type="submit" :disabled="admitForm.processing"
                    class="w-full py-3 rounded-lg bg-green-600 hover:bg-green-700 text-white
                           text-sm font-semibold flex justify-center items-center gap-2 transition">
                    <Spinner v-if="admitForm.processing" :size="14" />
                    Confirm Admission
                </button>
            </form>
        </Modal>

        <!-- ── Reject Modal ──────────────────────────────────────────────── -->
        <Modal v-model="rejectModal" title="Reject Application" @close="rejectModal = false">
            <form class="p-4 md:p-5" @submit.prevent="submitReject">
                <p class="text-sm text-gray-500 mb-4">
                    Rejecting
                    <strong>{{ selectedApp?.applicant?.first_name }} {{ selectedApp?.applicant?.last_name }}</strong>'s
                    application.
                </p>
                <Textarea label="Rejection Reason" v-model="rejectForm.rejection_reason"
                    :rows="4" placeholder="Provide a clear reason..."
                    :error="rejectForm.errors.rejection_reason" class="mb-4" />
                <button type="submit" :disabled="rejectForm.processing"
                    class="w-full py-3 rounded-lg bg-red-600 hover:bg-red-700 text-white
                           text-sm font-semibold flex justify-center items-center gap-2 transition">
                    <Spinner v-if="rejectForm.processing" :size="14" />
                    Confirm Rejection
                </button>
            </form>
        </Modal>

        <!-- ── Header ───────────────────────────────────────────────────── -->
        <div class="flex items-center justify-between mb-6">
            <div>
                <h2 class="text-xl font-bold text-primary">Applications</h2>
                <p class="text-sm text-gray-500 mt-0.5">Online applicant pipeline</p>
            </div>
        </div>

        <!-- ── Status Tabs ───────────────────────────────────────────────── -->
        <div class="flex items-center gap-2 mb-5 flex-wrap">
            <button
                v-for="s in [{ value: null, label: 'All' }, ...statuses.map(s => ({ value: s.value, label: s.value }))]"
                :key="s.value"
                @click="filters.status = s.value; applyFilters()"
                class="px-3 py-1.5 rounded-lg text-xs font-semibold border transition capitalize"
                :class="filters.status === s.value
                    ? 'bg-primary text-white border-primary'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-primary'"
            >
                {{ s.label }}
                <span v-if="s.value && counts?.[s.value]"
                    class="ml-1 bg-white/20 text-inherit px-1.5 py-0.5 rounded-full text-[10px]">
                    {{ counts[s.value] }}
                </span>
            </button>
        </div>

        <!-- ── Table ─────────────────────────────────────────────────────── -->
        <Table
            :data="applications"
            :columns="columns"
            row-key="id"
            :exportable="true"
            :actions="{ view: true }"
            :filters="[
                {
                    key: 'session_id', label: 'Session',
                    options: [{ label: 'All Sessions', value: null }, ...(sessions ?? []).map(s => ({ label: s.name, value: s.id }))],
                    value: filters.session_id,
                },
                {
                    key: 'section_id', label: 'Section',
                    options: [{ label: 'All Sections', value: null }, ...sectionOptions],
                    value: filters.section_id,
                },
            ]"
            @search="handleSearch"
            @filter="handleFilter"
            @view="id => router.get(`/admin/applications/${id}`)"
        >
            <template #applicant="{ row }">
                <div>
                    <p class="text-sm font-medium text-gray-800">
                        {{ row.applicant?.first_name }} {{ row.applicant?.last_name }}
                    </p>
                    <p class="text-xs text-gray-400">{{ row.applicant?.id }}</p>
                </div>
            </template>

            <template #section="{ row }">
                <span class="text-sm text-gray-600">{{ row.section?.name ?? '—' }}</span>
            </template>

            <template #session="{ row }">
                <span class="text-sm text-gray-600">{{ row.academic_session?.name ?? '—' }}</span>
            </template>

            <template #interview="{ row }">
                <span v-if="row.interview"
                    class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium capitalize"
                    :class="{
                        'bg-primary-50 text-primary-700':     row.interview.status === 'scheduled',
                        'bg-green-50 text-green-700':   row.interview.status === 'completed',
                        'bg-red-50 text-red-600':       row.interview.status === 'absent',
                        'bg-yellow-50 text-yellow-700': row.interview.status === 'rescheduled',
                    }">
                    <span class="w-1.5 h-1.5 rounded-full"
                        :class="{
                            'bg-primary-500':   row.interview.status === 'scheduled',
                            'bg-green-500':  row.interview.status === 'completed',
                            'bg-red-500':    row.interview.status === 'absent',
                            'bg-yellow-500': row.interview.status === 'rescheduled',
                        }"></span>
                    {{ row.interview.status }}
                </span>
                <span v-else class="text-xs text-gray-400">—</span>
            </template>

            <template #status="{ row }">
                <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize"
                    :class="statusColor(row.status)">
                    {{ row.status }}
                </span>
            </template>

            <template #submitted="{ row }">
                <span class="text-xs text-gray-500">
                    {{ row.submitted_at ? new Date(row.submitted_at).toLocaleDateString() : '—' }}
                </span>
            </template>

            <template #actions="{ row }">
                <div class="flex items-center gap-1.5 flex-wrap">

                    <!--
                        Schedule: submitted + no interview yet.
                        This is the trigger — scheduling moves the pipeline forward.
                        Recording and rescheduling happen on the Interviews page.
                    -->
                    <button v-if="row.status === 'submitted' && !row.interview"
                        @click="openSchedule(row)"
                        class="px-2.5 py-1 text-xs font-semibold rounded-lg
                               bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition">
                        Schedule
                    </button>

                    <!--
                        Admit: only after interview is fully recorded (status = interviewed).
                        Interview recording happens on the Interviews page first.
                    -->
                    <button v-if="row.status === 'interviewed'"
                        @click="openAdmit(row)"
                        class="px-2.5 py-1 text-xs font-semibold rounded-lg
                               bg-green-50 text-green-700 hover:bg-green-100 transition">
                        Admit
                    </button>

                    <!--
                        Reject: available at any active stage.
                        Not shown for already rejected, enrolled, or admitted applications.
                    -->
                    <button v-if="!['rejected', 'enrolled', 'admitted'].includes(row.status)"
                        @click="openReject(row)"
                        class="px-2.5 py-1 text-xs font-semibold rounded-lg
                               bg-red-50 text-red-600 hover:bg-red-100 transition">
                        Reject
                    </button>
                </div>
            </template>
        </Table>
    </Layout>
</template>